import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { COMPANY_CONFIG } from '../config/company';
import { getPricingDisplay, getPeriodRate, formatInstallFee } from './pricingDisplay';

const SECTION_CONTAINER_STYLE = {
  position: 'absolute',
  left: '-9999px',
  top: '0',
  width: '800px',
  backgroundColor: 'white',
  padding: '40px',
  fontFamily: 'Arial, sans-serif',
  color: '#333',
  lineHeight: '1.6',
};

const addHtmlSectionToPdf = async (pdf, html, { isFirstSection = false } = {}) => {
  const section = document.createElement('div');
  Object.assign(section.style, SECTION_CONTAINER_STYLE);
  section.innerHTML = html;
  document.body.appendChild(section);

  const canvas = await html2canvas(section, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    imageTimeout: 15000,
  });

  document.body.removeChild(section);

  const imgWidth = 210;
  const pageHeight = 295;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  if (!isFirstSection) {
    pdf.addPage();
  }

  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
};

export const generateBillboardPDF = async (item) => {

  // Get all images for the billboard
  const allImages = item.images || [];

  // Format functions
  const formatTrafficCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(0)}K`;
    }
    return count.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const pricingDisplay = getPricingDisplay(item.billingPeriod, item.excludePricingTiers);
  const pricingTablesHtml = item.pricing.directions.map((direction) => {
    const tierRows = pricingDisplay.tiers.map((tier) => {
      const tierData = direction[tier.key];
      const rate = getPeriodRate(tierData);
      return `
        <tr style="border-bottom: 1px solid #ecf0f1;">
          <td style="padding: 8px; border: 1px solid #ecf0f1; font-weight: bold; font-size: 12px;">${tier.label}</td>
          <td style="padding: 8px; text-align: right; border: 1px solid #ecf0f1; font-size: 12px;">${formatCurrency(rate)}</td>
          <td style="padding: 8px; text-align: right; border: 1px solid #ecf0f1; border-right: 2px solid #000; font-size: 12px;">${formatInstallFee(tierData.vinylInstall)}</td>
          <td style="padding: 8px; text-align: right; border: 1px solid #ecf0f1; font-size: 12px;">${formatCurrency(rate + tierData.vinylInstall)}</td>
          <td style="padding: 8px; text-align: right; border: 1px solid #ecf0f1; font-size: 12px;">${formatCurrency(rate)}</td>
          <td style="padding: 8px; text-align: right; border: 1px solid #ecf0f1; font-size: 12px;">${formatCurrency(rate)}</td>
        </tr>
      `;
    }).join('');

    return `
      <div style="margin-bottom: 20px;">
        <h4 style="background: #9B1B30; color: white; margin: 0; padding: 10px; font-size: 16px; font-weight: 600; text-align: center; border-radius: 4px 4px 0 0;">
          ${direction.direction}
        </h4>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; border: 1px solid #e9ecef;">
          <thead>
            <tr style="background-color: #3498db; color: white;">
              <th style="padding: 8px; text-align: left; border: 1px solid #2980b9; font-size: 12px;">Contract</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #2980b9; font-size: 12px;">${pricingDisplay.perPeriodLabel}</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #2980b9; font-size: 12px; border-right: 2px solid #fff;">Vinyl & Install</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #2980b9; font-size: 12px;">${pricingDisplay.firstPeriodLabel}</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #2980b9; font-size: 12px;">${pricingDisplay.secondPeriodLabel}</th>
              <th style="padding: 8px; text-align: right; border: 1px solid #2980b9; font-size: 12px;">${pricingDisplay.ongoingLabel}</th>
            </tr>
          </thead>
          <tbody>
            ${tierRows}
          </tbody>
        </table>
      </div>
    `;
  }).join('');

  const headerHtml = `
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #2c3e50; margin: 0; font-size: 28px; font-weight: bold;">${COMPANY_CONFIG.name}</h1>
      <p style="color: #7f8c8d; margin: 5px 0; font-size: 16px;">${COMPANY_CONFIG.tagline}</p>
      <div style="border-top: 2px solid #3498db; width: 100px; margin: 20px auto;"></div>
    </div>
  `;

  const infoAndPricingHtml = `
    ${headerHtml}
    <div style="margin-bottom: 30px;">
      <h2 style="color: #2c3e50; font-size: 24px; margin-bottom: 10px; border-bottom: 2px solid #3498db; padding-bottom: 5px;">
        Billboard Information
      </h2>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
        <div>
          <h3 style="color: #34495e; font-size: 20px; margin: 0 0 10px 0;">${item.name}</h3>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Sign #:</strong> ${item.signNumber}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Location:</strong> ${item.city}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Weekly Traffic:</strong> ${formatTrafficCount(item.weeklyTrafficCount)}</p>
        </div>
        <div>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Coordinates:</strong> ${item.latitude}, ${item.longitude}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Panel Size:</strong> ${item.panelSize}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Vinyl Size:</strong> ${item.vinylSize}</p>
          ${item.illuminated != null ? `<p style="margin: 5px 0; font-size: 16px;"><strong>Illuminated:</strong> ${item.illuminated ? 'Yes' : 'No'}</p>` : ''}
          ${item.billingPeriod === '4week' ? '<p style="margin: 5px 0; font-size: 16px;"><strong>Billing Period:</strong> 4-week periods</p>' : ''}
        </div>
      </div>
    </div>
    <div style="margin-bottom: 30px;">
      <h3 style="color: #2c3e50; font-size: 20px; margin-bottom: 15px; border-bottom: 1px solid #bdc3c7; padding-bottom: 5px;">
        Pricing & Availability
      </h3>
      ${pricingTablesHtml}
    </div>
  `;

  const imagesHtml = allImages.length > 0 ? `
    <div style="margin-bottom: 30px;">
      <h3 style="color: #2c3e50; font-size: 20px; margin-bottom: 15px; border-bottom: 1px solid #bdc3c7; padding-bottom: 5px;">
        Billboard Images
      </h3>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
        ${allImages.map((imageUrl, index) => `
          <div style="text-align: center; border: 1px solid #e9ecef; border-radius: 8px; padding: 10px; background: #f8f9fa;">
            <img
              src="${imageUrl}"
              alt="Billboard Image ${index + 1}"
              style="width: 100%; max-width: 200px; height: 150px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;"
            />
            <p style="margin: 0; font-size: 12px; color: #666;">Image ${index + 1}</p>
          </div>
        `).join('')}
      </div>
    </div>
  ` : '';

  const contactHtml = `
    <div style="margin-bottom: 30px;">
      <h3 style="color: #2c3e50; font-size: 20px; margin-bottom: 15px; border-bottom: 1px solid #bdc3c7; padding-bottom: 5px;">
        Contact Information
      </h3>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
        <div>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Phone:</strong> ${COMPANY_CONFIG.contact.phone}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Email:</strong> ${COMPANY_CONFIG.contact.email}</p>
        </div>
        <div>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Business Hours:</strong> ${COMPANY_CONFIG.contact.businessHours}</p>
          <p style="margin: 5px 0; font-size: 16px;"><strong>Service Area:</strong> ${COMPANY_CONFIG.contact.serviceArea}</p>
        </div>
      </div>
    </div>
    <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #3498db;">
      <p style="color: #7f8c8d; font-size: 14px; margin: 0;">
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
      </p>
      <p style="color: #7f8c8d; font-size: 12px; margin: 5px 0 0 0;">
        ${COMPANY_CONFIG.name} - ${COMPANY_CONFIG.description}
      </p>
    </div>
  `;

  try {
    const imagePromises = allImages.map((imageUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = imageUrl;
      });
    });

    await Promise.all(imagePromises);

    const pdf = new jsPDF('p', 'mm', 'a4');

    if (imagesHtml) {
      await addHtmlSectionToPdf(pdf, infoAndPricingHtml, { isFirstSection: true });
      await addHtmlSectionToPdf(pdf, imagesHtml, { isFirstSection: false });
      await addHtmlSectionToPdf(pdf, contactHtml, { isFirstSection: false });
    } else {
      await addHtmlSectionToPdf(pdf, infoAndPricingHtml + contactHtml, { isFirstSection: true });
    }

    const fileName = `${item.name.replace(/\s+/g, '_')}_${item.signNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
}; 